import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, ShieldCheck, Phone, MapPin } from 'lucide-react';
import { SIDEBAR_MENU } from '../data';
import { useSiteInfo } from '../context/SiteContext';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileHnOpen, setMobileHnOpen] = useState(false);
  const [mobileKcnOpen, setMobileKcnOpen] = useState(false);
  const [mobileClnOpen, setMobileClnOpen] = useState(false);
  const { siteInfo } = useSiteInfo();

  return (
    <>
    <div className="h-16 md:hidden" aria-hidden="true" />
    <header className="bg-white shadow-md fixed inset-x-0 top-0 md:sticky z-50">

      {/* TOP BAR — Địa chỉ & Hotline */}
      <div className="hidden md:block bg-primary-dark text-white text-sm">
        <div className="container mx-auto px-4 py-2 flex justify-between items-center">
          <div className="flex items-center gap-2 opacity-90">
            <MapPin size={14} className="flex-shrink-0 text-slate-300" />
            <span className="truncate">{siteInfo.address}</span>
          </div>
          <a
            href={`tel:${siteInfo.phone?.replace(/\./g, '').replace(/ /g, '')}`}
            className="flex items-center gap-2 font-semibold text-orange-300 hover:text-orange-200 transition-colors whitespace-nowrap"
          >
            <Phone size={14} className="flex-shrink-0 animate-pulse" />
            Hotline: {siteInfo.phone}
          </a>
        </div>
      </div>

      {/* LOGO ROW */}
      <div className="container mx-auto px-4 py-3 h-16 md:h-auto md:min-h-[120px] flex justify-between items-center">
        <Link to="/" onClick={() => window.scrollTo(0, 0)} className="flex items-center w-[80vw] md:w-auto">
          <img
            src="/assets/banners/header-navy-gold-20261008.svg"
            width={1099}
            height={127}
            fetchPriority="high"
            decoding="sync"
            alt={`Logo ${siteInfo.name}`}
            className="h-auto max-h-16 md:max-h-24 w-full md:w-auto aspect-[1099/127] object-contain"
          />
        </Link>

        {/* Quality Badge - Hidden on mobile */}
        <div className="hidden md:flex items-center space-x-3 bg-slate-50/50 border border-accent/50 py-1.5 px-4 rounded-lg shadow-sm">
          <div className="bg-primary-dark p-1.5 rounded-full text-white ring-1 ring-accent">
            <ShieldCheck size={20} />
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-none mb-1">Cam kết 100%</span>
            <span className="text-sm font-black text-primary uppercase leading-none">Uy Tín - Chất Lượng</span>
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-gray-700"
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Desktop Nav Row */}
      <nav className="hidden md:block bg-primary border-b-2 border-accent">
        <div className="container mx-auto px-2 flex items-center justify-center space-x-2 lg:space-x-4 font-medium text-white text-[14px] lg:text-base">
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="hover:bg-primary-dark transition-colors px-4 py-4 uppercase whitespace-nowrap">Trang chủ</Link>
          <Link to="/gioi-thieu" className="hover:bg-primary-dark transition-colors px-4 py-4 uppercase whitespace-nowrap">Giới thiệu</Link>

          {/* Khoan Cọc Nhồi Dropdown */}
          <div className="relative group">
            <span className="hover:bg-primary-dark transition-colors cursor-pointer flex items-center px-4 py-4 uppercase whitespace-nowrap">
              KHOAN CỌC NHỒI <ChevronDown size={18} className="ml-1 transition-transform group-hover:rotate-180 duration-200" />
            </span>
            <div className="absolute top-full left-0 w-64 hidden group-hover:block z-50">
              <div className="bg-white shadow-xl rounded-b-md overflow-hidden border border-gray-100 max-h-96 overflow-y-auto mt-0">
                {SIDEBAR_MENU.filter(s => !s.title.includes('Tại') && s.title.includes('Khoan Cọc Nhồi')).map((service, index) => (
                  <Link
                    key={index}
                    to={service.link}
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-slate-50 hover:text-primary border-b last:border-0 transition-colors"
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Tại Hà Nội Dropdown */}
          <div className="relative group">
            <span className="hover:bg-primary-dark transition-colors cursor-pointer flex items-center px-4 py-4 uppercase whitespace-nowrap">
              KCN TẠI HÀ NỘI <ChevronDown size={18} className="ml-1 transition-transform group-hover:rotate-180 duration-200" />
            </span>
            <div className="absolute top-full left-0 w-56 hidden group-hover:block z-50">
              <div className="bg-white shadow-xl rounded-b-md overflow-hidden border border-gray-100 max-h-96 overflow-y-auto mt-0">
                {SIDEBAR_MENU.filter(s => s.title.includes('Tại')).map((service, index) => (
                  <Link
                    key={index}
                    to={service.link}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-slate-50 hover:text-primary border-b last:border-0 transition-colors"
                  >
                    {service.title.replace('Khoan Cọc Nhồi Tại ', 'Tại ')}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Chống Lún Nhà Dropdown */}
          <div className="relative group">
            <span className="hover:bg-primary-dark transition-colors cursor-pointer flex items-center px-4 py-4 uppercase whitespace-nowrap">
              CHỐNG LÚN NHÀ <ChevronDown size={18} className="ml-1 transition-transform group-hover:rotate-180 duration-200" />
            </span>
            <div className="absolute top-full left-0 w-56 hidden group-hover:block z-50">
              <div className="bg-white shadow-xl rounded-b-md overflow-hidden border border-gray-100 max-h-96 overflow-y-auto mt-0">
                {SIDEBAR_MENU.filter(s => s.title.includes('Chống')).map((service, index) => (
                  <Link
                    key={index}
                    to={service.link}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-slate-50 hover:text-primary border-b last:border-0 transition-colors"
                  >
                    {service.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link to="/tin-tuc" className="hover:bg-primary-dark transition-colors px-4 py-4 uppercase whitespace-nowrap">Tin tức</Link>
          {siteInfo.showCompanyProfile !== false && (
            <Link to="/ho-so-nang-luc" className="hover:bg-primary-dark transition-colors px-4 py-4 uppercase whitespace-nowrap">Hồ sơ năng lực</Link>
          )}
          <Link to="/lien-he" className="hover:bg-primary-dark transition-colors px-4 py-4 uppercase whitespace-nowrap">Liên hệ</Link>
        </div>
      </nav>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg max-h-[calc(100dvh-64px)] overflow-y-auto overscroll-contain">
          {/* Mobile Hotline Bar */}
          <div className="bg-primary-dark text-white px-4 py-2 flex items-center justify-between text-sm">
            <span className="opacity-75 truncate text-xs">{siteInfo.address}</span>
            <a href={`tel:${siteInfo.phone?.replace(/\./g, '').replace(/ /g, '')}`} className="text-yellow-300 font-bold whitespace-nowrap ml-2 flex items-center gap-1">
              <Phone size={12} /> {siteInfo.phone}
            </a>
          </div>
          <nav className="flex flex-col px-4 py-2 space-y-2">
            <Link to="/" onClick={() => { window.scrollTo(0, 0); setIsMenuOpen(false); }} className="py-2 border-b uppercase font-medium">Trang chủ</Link>
            <Link to="/gioi-thieu" onClick={() => setIsMenuOpen(false)} className="py-2 border-b uppercase font-medium">Giới thiệu</Link>

            {/* Mobile KHOAN CỌC NHỒI */}
            <button
              onClick={() => setMobileKcnOpen(!mobileKcnOpen)}
              className="py-2 border-b font-medium text-primary uppercase flex items-center justify-between"
            >
              KHOAN CỌC NHỒI <ChevronDown size={16} className={`transition-transform duration-200 ${mobileKcnOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileKcnOpen && (
              <div className="pl-4 flex flex-col space-y-2 border-b pb-2 mt-2 max-h-64 overflow-y-auto border-l-2 border-l-primary">
                {SIDEBAR_MENU.filter(s => !s.title.includes('Tại') && s.title.includes('Khoan Cọc Nhồi')).map((service, index) => (
                  <Link
                    key={index}
                    to={service.link}
                    onClick={() => setIsMenuOpen(false)}
                    className="text-sm text-gray-600 py-1 hover:text-primary"
                  >
                    › {service.title}
                  </Link>
                ))}
              </div>
            )}

            {/* Mobile KHOAN TẠI HÀ NỘI */}
            <button
              onClick={() => setMobileHnOpen(!mobileHnOpen)}
              className="py-2 border-b font-medium text-primary uppercase flex items-center justify-between"
            >
              KCN TẠI HÀ NỘI <ChevronDown size={16} className={`transition-transform duration-200 ${mobileHnOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileHnOpen && (
              <div className="pl-4 flex flex-col space-y-2 border-b pb-2 mt-2 max-h-64 overflow-y-auto border-l-2 border-l-primary">
                {SIDEBAR_MENU.filter(s => s.title.includes('Tại')).map((service, index) => (
                  <Link
                    key={index}
                    to={service.link}
                    onClick={() => setIsMenuOpen(false)}
                    className="text-sm text-gray-600 py-1 hover:text-primary"
                  >
                    › {service.title.replace('Khoan Cọc Nhồi Tại ', 'Tại ')}
                  </Link>
                ))}
              </div>
            )}

            {/* Mobile CHỐNG LÚN NHÀ */}
            <button
              onClick={() => setMobileClnOpen(!mobileClnOpen)}
              className="py-2 border-b font-medium text-primary uppercase flex items-center justify-between"
            >
              CHỐNG LÚN NHÀ <ChevronDown size={16} className={`transition-transform duration-200 ${mobileClnOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileClnOpen && (
              <div className="pl-4 flex flex-col space-y-2 border-b pb-2 mt-2 border-l-2 border-l-primary">
                <Link to="/chong-lun-nha" onClick={() => setIsMenuOpen(false)} className="text-sm text-gray-600 py-1 hover:text-primary">
                  › Dịch vụ chống lún nhà
                </Link>
                <Link to="/vang-chong-va-ep-cu-u200" onClick={() => setIsMenuOpen(false)} className="text-sm text-gray-600 py-1 hover:text-primary">
                  › Chống văng & ép cừ U200
                </Link>
              </div>
            )}

            <Link to="/tin-tuc" onClick={() => setIsMenuOpen(false)} className="py-2 border-b uppercase font-medium">Tin tức</Link>
            {siteInfo.showCompanyProfile !== false && (
              <Link to="/ho-so-nang-luc" onClick={() => setIsMenuOpen(false)} className="py-2 border-b uppercase font-medium">Hồ sơ năng lực</Link>
            )}
            <Link to="/lien-he" onClick={() => setIsMenuOpen(false)} className="py-2 uppercase font-medium">Liên hệ</Link>
          </nav>
        </div>
      )}
    </header>
    </>
  );
}
